import {MainLayout} from "../components/layouts/MainLayout.tsx";
import {useParams, useSearchParams} from "react-router-dom";
import {useContext, useEffect, useRef, useState} from "react";
import {ChannelsContext} from "../contexts/ChannelsContext.tsx";
import {AppMainStateContext} from "../contexts/AppMainStateContext.tsx";
import {IChannel} from "../interfaces/IChannel.ts";
import * as React from "react";
import UtilsHelper from "../helpers/utilsHelper.ts";
import apiNet from "../newApi/apiNet.ts";
import {ChevronLeftIcon, ChevronRightIcon, ChevronUpDownIcon} from "@heroicons/react/24/solid";
import {LazyLoadImage} from "react-lazy-load-image-component";
import DateHelper from "../helpers/dateHelper.ts";
import PlayButton from "../components/buttons/PlayButton.tsx";
import ClassificationIcon from "../components/icons/ClassificationIcon.tsx";
import Popup, {IPopupRef} from "../components/popups/Popup.tsx";
import ButtonPopup from "../components/buttons/ButtonPopup.tsx";
import {IChannelDetails} from "../interfaces/IChannelDetails.ts";
import {AxiosHeaders} from "axios";
import Button from "../components/buttons/Button.tsx";
import {ButtonSize, ButtonVariant} from "../types/button.ts";
import {PopupItemButton} from "../components/popups/PopupItemButton.tsx";

type buttonMode = "PREV" | "NEXT";

interface ChangeDatePopupItem {
    name: string,
    since: string
}

const NewChannelDetailsPage = () => {
    const {ch_id} = useParams();
    const [channelId, setChannelId] = useState(Number(ch_id));
    const [searchParams] = useSearchParams();
    const [since, setSince] = useState(searchParams.get("since"));
    const [selectedItemIndex, setSelectedItemIndex] = useState(-1);
    const {channels, getChannels} = useContext(ChannelsContext);
    const {isMobile} = useContext(AppMainStateContext);
    const [channel, setChannel] = useState<IChannelDetails>();

    const scrollXRef = useRef<HTMLDivElement>(null);
    const [scrollPositionX, setScrollPositionX] = useState(0);

    const popupRef = useRef<IPopupRef>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [changeDatePopupItems, setChangeDatePopupItems] = useState<ChangeDatePopupItem[]>();
    const [isEpgDetailsMode, setIsEpgDetailsMode] = useState(false);

    const handleButtonOnClick = () => {
        if (isOpen && popupRef.current) {
            popupRef.current.closePopup();
            setSelectedItemIndex(-1);
        } else
            popupRef.current?.togglePopup();
    }

    const scrollFunc = (mode: buttonMode) => {
        let scrollWidth = 0;
        if (scrollXRef.current) scrollWidth = scrollXRef.current.scrollWidth;
        let newPositionX = 0;
        if (mode === "NEXT") {
            newPositionX = scrollPositionX + (isMobile ? 200 : 600);
            if (scrollWidth && newPositionX >= scrollWidth) newPositionX = scrollWidth;
        } else {
            newPositionX = scrollPositionX - (isMobile ? 200 : 600);
            if (newPositionX < 0) newPositionX = 0;
        }
        scrollXRef?.current?.scrollTo({left: newPositionX, behavior: "smooth"});
    };

    const handleScrollX = (event: React.UIEvent<HTMLElement>) => {
        setScrollPositionX(event.currentTarget.scrollLeft);
    };

    const handleButtonClick = (channel: IChannel) => {
        setChannelId(channel.ch_id);
        scrollToTile(channel.ch_id, true);
        setSelectedItemIndex(-1);
    }

    const scrollToTile = (channelId: number, smooth: boolean) => {
        const tileElem = scrollXRef.current?.querySelector("#tile-" + channelId) as HTMLElement;
        if (tileElem) {
            const scrollLeft = tileElem.offsetLeft - ((window.innerWidth - 96 - (isMobile ? -10 : 100)) / 2 + 16);
            if (scrollXRef.current)
                scrollXRef.current.scrollTo({left: scrollLeft, behavior: smooth ? "smooth" : "instant"});
        }
    }

    const setChangeDataPopupItems = (fromUnixTimestamp: number, toUnixTimestamp: number) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayTimestamp = today.getTime() / 1000;
        const changeDatePopupItems: ChangeDatePopupItem[] = [];
        changeDatePopupItems.push({name: getDateName(todayTimestamp), since: getSince(todayTimestamp)})
        const numberOfPreviousDays = Math.ceil((todayTimestamp - fromUnixTimestamp) / 86400);
        const numberOfNextDays = Math.ceil((toUnixTimestamp - (todayTimestamp + 86400)) / 86400);
        for (let i = 0; i < numberOfPreviousDays; i++) {
            const timestamp = todayTimestamp - (i + 1) * 86400;
            changeDatePopupItems.unshift({name: getDateName(timestamp), since: getSince(timestamp)})
        }
        for (let i = 0; i < numberOfNextDays; i++) {
            const timestamp = todayTimestamp + (i + 1) * 86400;
            changeDatePopupItems.push({name: getDateName(timestamp), since: getSince(timestamp)})
        }
        setChangeDatePopupItems(changeDatePopupItems);
    }

    const getSince = (unixTimestamp: number): string => {
        const date = new Date(unixTimestamp * 1000);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    }

    const getDateName = (unixTimestamp: number): string => {
        const dateFromTimestamp = new Date(unixTimestamp * 1000);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayTimestamp = today.getTime() / 1000;
        const dayOfWeekIndex = new Date(unixTimestamp * 1000).getDay();
        if (unixTimestamp >= (todayTimestamp) && unixTimestamp < todayTimestamp + (60 * 60 * 24))
            return "Dziś";
        else if (unixTimestamp >= (todayTimestamp - (60 * 60 * 24)) && unixTimestamp < todayTimestamp)
            return "Wczoraj";
        else if (unixTimestamp >= (todayTimestamp + (60 * 60 * 24)) && unixTimestamp < (todayTimestamp + 2 * (60 * 60 * 24)))
            return "Jutro";
        else {
            switch (dayOfWeekIndex) {
                case 0:
                    return `Niedziela ${String(dateFromTimestamp.getDate()).padStart(2, "0")}.${String(dateFromTimestamp.getMonth() + 1).padStart(2, "0")}`;
                case 1:
                    return `Poniedziałek ${String(dateFromTimestamp.getDate()).padStart(2, "0")}.${String(dateFromTimestamp.getMonth() + 1).padStart(2, "0")}`;
                case 2:
                    return `Wtorek ${String(dateFromTimestamp.getDate()).padStart(2, "0")}.${String(dateFromTimestamp.getMonth() + 1).padStart(2, "0")}`;
                case 3:
                    return `Środa ${String(dateFromTimestamp.getDate()).padStart(2, "0")}.${String(dateFromTimestamp.getMonth() + 1).padStart(2, "0")}`;
                case 4:
                    return `Czwartek ${String(dateFromTimestamp.getDate()).padStart(2, "0")}.${String(dateFromTimestamp.getMonth() + 1).padStart(2, "0")}`;
                case 5:
                    return `Piątek ${String(dateFromTimestamp.getDate()).padStart(2, "0")}.${String(dateFromTimestamp.getMonth() + 1).padStart(2, "0")}`;
                case 6:
                    return `Sobota ${String(dateFromTimestamp.getDate()).padStart(2, "0")}.${String(dateFromTimestamp.getMonth() + 1).padStart(2, "0")}`;
                default:
                    return "";
            }
        }
    }

    useEffect(() => {
        getChannels();
    }, []);

    useEffect(() => {

        (async () => {
            const {data, headers} = await apiNet.getChannel(channelId, since ? new Date(since) : new Date());
            if (since === null) {
                const date = new Date();
                setSince(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`);
            }
            if (headers instanceof AxiosHeaders
                && headers.has('x-start-timestamp')
                && headers.has('x-end-timestamp')
            ) {
                const startTimestamp = headers.get('x-start-timestamp');
                const endTimestamp = headers.get('x-end-timestamp');
                setChangeDataPopupItems(Number(startTimestamp), Number(endTimestamp));
            }

            if (data && data.epgList) {
                const index = data.epgList.findIndex(x => DateHelper.GetHourAndMinuteFromUnixTimespanDate(x.startTimestamp)! <= DateHelper.GetHourAndMinuteFromDate(new Date()) && DateHelper.GetHourAndMinuteFromUnixTimespanDate(x.endTimestamp)! > DateHelper.GetHourAndMinuteFromDate(new Date()));
                setSelectedItemIndex(index);
            }
            setChannel(data);
            UtilsHelper.changeWindowDocumentTitleAndHistoryPath(`${data.name} Program`, `/kanaly-tv/${data.channelId}?since=${since}`);
        })
        ()
        setTimeout(() => {
            scrollToTile(channelId, false);
        }, 400)

    }, [channelId, since])

    return (
        <MainLayout>
            <div className={"flex flex-1 flex-col h-full w-full md:pt-0"}>
                <div className={"w-full sticky top-14 md:top-0 bg-neutral-800 z-10 py-2 md:py-5"}>
                    <div className={"relative flex items-center"}>
                        <div className={"absolute w-16 h-full bg-neutral-800 z-10"}>
                            <button
                                className={"absolute top-1/2 -translate-y-1/2 w-9 h-9 bg-neutral-800 rounded-full border-2 border-neutral-200 ml-4 flex justify-center items-center"}
                                onClick={() => {
                                    scrollFunc("PREV")
                                }}
                            >
                                <ChevronLeftIcon className={"w-9 h-9 pl-0.5 pr-1"}/>
                            </button>
                        </div>
                        <div
                            onScroll={(e) => handleScrollX(e)}
                            ref={scrollXRef}
                            className="scrollbar-hide flex space-x-4 w-full overflow-x-auto mb-0 h-20 md:h-28 items-center pr-[4.5rem]"
                        >
                            {channels.length > 0 ?
                                channels.filter(x => x.enabled).map((item) =>
                                    <button
                                        key={item.ch_id}
                                        id={"tile-" + item.ch_id}
                                        className={`flex flex-col snap-center ml-20`}
                                        onClick={() => handleButtonClick(item)}
                                    >
                                        <LazyLoadImage
                                            alt=""
                                            className="h-full w-full"
                                            effect="opacity"
                                            wrapperClassName={`h-16 md:h-20 aspect-square p-2 border rounded-lg ${item.ch_id === channelId ? "border-neutral-600 scale-125 bg-neutral-950 shadow-3xl" : "border-neutral-600 scale-100"}  shadow-neutral-700 transition transform duration-500`}
                                            src={`${import.meta.env.VITE_MIX_CHANNEL_LOGO_URL}/${item.logo}`}
                                        />
                                    </button>
                                ) :
                                <>
                                    {[...Array(20)].map((_, i) => (
                                        <div
                                            key={i}
                                            className={"h-20 w-20 animate-pulse shrink-0 bg-neutral-600 rounded-lg"}
                                        />
                                    ))}
                                </>
                            }
                        </div>
                        <div className={"absolute w-14 h-full bg-neutral-800 z-10 right-0"}>
                            <button
                                className={"absolute top-1/2 -translate-y-1/2 w-9 h-9 bg-neutral-800 rounded-full border-2 border-neutral-200 ml-3 flex justify-center items-center text-neutral-200 hover:text-white"}
                                onClick={() => {
                                    scrollFunc("NEXT")
                                }}
                            >
                                <ChevronRightIcon className={"w-9 h-9 pr-0.5 pl-1"}/>
                            </button>
                        </div>
                    </div>
                </div>
                {isMobile &&
                    <div className={"grow"}>
                        {channel &&
                            <div
                                className={"px-4 sticky top-[9.5rem] bg-neutral-800 z-10 pb-3 md:pb-4 border-b border-neutral-700 shadow shadow-neutral-700 animate-opacityEaseIn"}
                                key={channel.channelId}
                            >
                                <div className={"relative"}>
                                    <h3 className={"text-xl mb-2 pt-1"}>Aktualnie:</h3>
                                    {channel.actualEpg ?
                                        <div className={"space-y-0"}>
                                            <p className={"text-[#008dd2] font-bold text-lg"}>{DateHelper.GetHourAndMinuteFromUnixTimespanDate(channel.actualEpg.startTimestamp)} - {DateHelper.GetHourAndMinuteFromUnixTimespanDate(channel.actualEpg.endTimestamp)}</p>
                                            <p className={`text-2xl md:text-3xl font-bold line-clamp-2`}>{channel.actualEpg.name || "Brak danych"}</p>
                                            <p className={"text-neutral-400"}>{channel.actualEpg.category}</p>
                                        </div> :
                                        <p className={"my-4 text-neutral-400"}>Brak informacji</p>
                                    }
                                    {channel.enabled &&
                                        <PlayButton
                                            channel={{
                                                ch_id: channel.channelId,
                                                name: channel.name,
                                                enabled: channel.enabled
                                            }}
                                            withUnavailableModal={true}
                                            loggedInElem={
                                                <span
                                                    className={"inline-block rounded-full bg-[#008DD2] border border-[#008dd2] hover:border-[#007CC1] hover:bg-[#007CC1] transition-colors py-1 px-4 focus:outline-none font-light tracking-wider mt-2"}>Oglądaj</span>
                                            }
                                            loggedOutElem={
                                                <span
                                                    className={"inline-block rounded-full bg-[#008DD2] border border-[#008dd2] hover:border-[#007CC1] hover:bg-[#007CC1] transition-colors py-1 px-4 focus:outline-none font-light tracking-wider mt-2"}>Zaloguj się</span>
                                            }
                                            className={"focus:outline-none"}/>
                                    }
                                    {changeDatePopupItems &&
                                        <div className={"absolute right-0 -bottom-8"}>
                                            <ButtonPopup
                                                className={`flex justify-between items-center min-w-28 ${!isOpen && "bg-neutral-900"} px-4 font-light tracking-wider min-h-9 border-2 border-neutral-700 shadow-3xl shadow-neutral-700 transition duration-300 opacity-0 ${!isEpgDetailsMode && "opacity-100"}`}
                                                isOpen={isOpen}
                                                onClick={handleButtonOnClick}
                                            >
                                                {changeDatePopupItems[changeDatePopupItems.findIndex(x => x.since === since)]?.name || since || ""}
                                                <ChevronUpDownIcon
                                                    className={`w-6 h-6 ml-2 transition-colors ${isOpen ? "text-neutral-200" : "text-neutral-500"}`}/>
                                            </ButtonPopup>
                                            <Popup
                                                className={"right-0 shadow-3xl shadow-neutral-600"}
                                                isOpen={isOpen}
                                                ref={popupRef}
                                                title={"Wybierz dzień"}
                                                setIsOpen={(isOpen: boolean) => setIsOpen(isOpen)}
                                            >
                                                {changeDatePopupItems?.map(item =>
                                                    <PopupItemButton
                                                        id={item.name}
                                                        key={item.name}
                                                        handleClick={() => {
                                                            setSince(item.since);
                                                            popupRef.current?.closePopup();
                                                        }}
                                                    >{item.name}</PopupItemButton>
                                                )}
                                            </Popup>
                                        </div>
                                    }
                                </div>
                            </div>}


                        {channel &&
                            <div
                                className={"w-full overflow-y-scroll overflow-x-hidden px-4 animate-opacityEaseIn mt-0 scrollbar-hide"}
                                key={`channelList${channel.channelId}`}
                            >
                                <div
                                    key={since}
                                    className={`animate-opacityEaseIn w-full md:w-1/2 md:px-4 mt-4 ${!isEpgDetailsMode ? "translate-x-0" : "-translate-x-full max-h-0 md:translate-x-0"} transition transform duration-500 shrink-0 md:overflow-y-scroll md:h-full md:border-l-2 border-neutral-600`}
                                >
                                    {!isEpgDetailsMode && channel.epgList && channel.epgList.length > 0 ?
                                        channel.epgList.map((epg, i) =>
                                            <button
                                                key={i}
                                                className={`flex items-start py-2 space-x-2 w-full ${!isEpgDetailsMode ? "opacity-100" : "opacity-0"} transition-opacity`}
                                                onClick={() => {
                                                    const htmlElem = document.querySelector("main");
                                                    htmlElem?.scrollTo({top: 0, behavior: "instant"});
                                                    setIsEpgDetailsMode(true);
                                                    setSelectedItemIndex(i)
                                                }}
                                            >
                                                <div
                                                    className={`font-bold ${epg.endTimestamp < DateHelper.GetDateTimeNowAsUnixTimespan() ? "text-[#008dd2]/60" : "text-[#008dd2]"}`}>
                                                    {DateHelper.GetHourAndMinuteFromUnixTimespanDate(epg.startTimestamp)}
                                                </div>
                                                <div
                                                    className={`flex flex-col text-left border-l border-neutral-600 pl-2 ${epg.endTimestamp < DateHelper.GetDateTimeNowAsUnixTimespan() ? "text-neutral-100/60" : "text-neutral-100"}`}>
                                                    <p>{epg.name}</p>
                                                    <p>{epg.category}</p>
                                                </div>
                                            </button>
                                        ) :
                                        <p className={"text-center pt-6 text-neutral-400"}>
                                            Brak danych na dzień {since}, <br/>zmień zakres czasu.</p>}
                                    <div
                                        className={"w-full h-0.5 bg-gradient-to-r from-neutral-700 to-transparent my-6"}/>
                                </div>
                                <div
                                    className={`${isEpgDetailsMode ? "translate-x-0" : "translate-x-full h-0"} transition transform duration-500 shrink-0 w-full md:w-1/2`}
                                    onAnimationStart={() => {
                                        const htmlElem = document.querySelector("html");
                                        htmlElem?.scrollTo({top: 0, behavior: 'smooth'})
                                    }}
                                >
                                    {isEpgDetailsMode &&
                                        <>
                                            <Button
                                                className={"flex justify-center pl-2 text-neutral-100 ring-offset-0 border-0 border-neutral-500 ring-0 font-light tracking-wider"}
                                                variant={ButtonVariant.TERTIARY}
                                                size={ButtonSize.BASE}
                                                onClick={() => {
                                                    setSelectedItemIndex(-1);
                                                    setIsEpgDetailsMode(false)
                                                }}
                                            ><ChevronLeftIcon className={"w-6 h-6"}/> powrót</Button>
                                            <p className={"text-[#008dd2] font-bold text-lg mt-4 tracking-wider"}>
                                                {DateHelper.GetHourAndMinuteFromUnixTimespanDate(channel.epgList?.[selectedItemIndex]?.startTimestamp || 0)} - {DateHelper.GetHourAndMinuteFromUnixTimespanDate(channel.epgList?.[selectedItemIndex]?.endTimestamp || 0)}
                                            </p>
                                            <p className={"text-2xl md:text-3xl font-bold mt-2"}>{channel?.epgList?.[selectedItemIndex]?.name}</p>
                                            <span className={"flex space-x-2 items-center mt-0.5"}>
                                                <p className={"text-neutral-400"}>{channel?.epgList?.[selectedItemIndex]?.category}</p>
                                                {channel.epgList?.[selectedItemIndex]?.rating !== 0 &&
                                                    <>
                                                        <span className={"h-1 w-1 rounded-full bg-neutral-600"}/>
                                                        <ClassificationIcon
                                                            age={channel.epgList?.[selectedItemIndex]?.rating || 0}
                                                            className={"w-5 shrink-0"}
                                                        />
                                                    </>
                                                }
                                            </span>
                                            <div className={"w-20 h-1 bg-[#008dd2] rounded-full my-4"}/>
                                            <p className={"mt-4"}>{channel?.epgList?.[selectedItemIndex]?.description}</p>
                                            <div
                                                className={"w-full h-0.5 bg-gradient-to-r from-neutral-700 to-transparent my-6"}/>
                                        </>
                                    }
                                </div>
                            </div>}
                    </div>
                }
                {!isMobile &&
                    <>
                        <div
                            key={channel?.channelId}
                            className={"w-full animate-opacityEaseIn"}
                        >
                            {channel &&
                                <div
                                    className={"px-4 sticky top-44 md:top-[9.5rem] bg-neutral-800 z-10 pb-3 md:pb-4 border-b-0 border-neutral-500 shadow"}>
                                    <h3 className={"text-xl mb-2"}>Aktualnie:</h3>
                                    {channel.actualEpg ?
                                        <>
                                            <p className={"text-[#008dd2] font-bold text-lg"}>
                                                {DateHelper.GetHourAndMinuteFromUnixTimespanDate(channel.actualEpg.startTimestamp)} - {DateHelper.GetHourAndMinuteFromUnixTimespanDate(channel.actualEpg.endTimestamp)}
                                            </p>
                                            <p className={`text-2xl md:text-3xl font-bold line-clamp-2`}>{channel.actualEpg.name || "Brak danych"}</p>
                                            <p className={"mb-2 text-neutral-400"}>{channel.actualEpg.category}</p>
                                        </>
                                        :
                                        <p className={"my-5"}>Brak informacji</p>
                                    }
                                    {channel.enabled &&
                                        <PlayButton
                                            withUnavailableModal={true}
                                            loggedInElem={
                                                <span
                                                    className={"inline-block rounded-full bg-[#008DD2] border border-[#008dd2] hover:border-[#007CC1] hover:bg-[#007CC1] transition-colors py-1 px-4 focus:outline-none font-light tracking-wider "}>Oglądaj
                                                </span>
                                            }
                                            loggedOutElem={
                                                <span
                                                    className={"inline-block rounded-full bg-[#008DD2] border border-[#008dd2] hover:border-[#007CC1] hover:bg-[#007CC1] transition-colors py-1 px-4 focus:outline-none font-light tracking-wider "}>Zaloguj się
                                                </span>
                                            }
                                            channel={{
                                                ch_id: channel.channelId,
                                                name: channel.name,
                                                enabled: channel.enabled
                                            }}
                                            className={"pt-1 focus:outline-none"}
                                        />
                                    }
                                </div>
                            }
                        </div>
                        <div
                            className={"flex-grow w-full max-w-7xl relative py-2 pr-2 pl-4 animate-opacityEaseIn min-h-96"}
                            key={channel?.name}
                        >
                            <div
                                className={"absolute ml-4 my-2 rounded-l-3xl bg-gradient-to-r from-neutral-500 to-transparent top-0 bottom-0 left-0 right-0"}/>
                            <div
                                className={"absolute ml-4 my-2 rounded-l-3xl top-0.5 left-0.5 right-0 bottom-0.5 z-20 bg-neutral-800 flex py-5"}>
                                <div className={"h-full overflow-y-scroll w-1/3 border-r border-neutral-700"}>
                                    <div className={"sticky top-1 inline-block left-1/2 -translate-x-1/2"}>
                                        {changeDatePopupItems &&
                                            <>
                                                <ButtonPopup
                                                    className={`min-w-28 ${isOpen ? "bg-" : "bg-neutral-900"} min-h-9 border border-neutral-400 shadow-3xl shadow-neutral-400`}
                                                    isOpen={isOpen}
                                                    onClick={handleButtonOnClick}
                                                >
                                                    {changeDatePopupItems[changeDatePopupItems.findIndex(x => x.since === since)]?.name || since || ""}
                                                </ButtonPopup>
                                                <Popup
                                                    isOpen={isOpen}
                                                    ref={popupRef}
                                                    title={"Wybierz dzień"}
                                                    className={"left-1/2 top-10 -translate-x-1/2"}
                                                    setIsOpen={(isOpen: boolean) => setIsOpen(isOpen)}
                                                >
                                                    {changeDatePopupItems?.map(item =>
                                                        <PopupItemButton
                                                            id={item.name}
                                                            key={item.name}
                                                            handleClick={() => {
                                                                setSince(item.since);
                                                                popupRef.current?.closePopup();
                                                            }}
                                                        >{item.name}</PopupItemButton>
                                                    )}
                                                </Popup>
                                            </>
                                        }
                                    </div>
                                    {channel && channel.epgList && channel.epgList.length > 0 ? channel.epgList.map((epg, i) =>
                                            <button
                                                key={i}
                                                className={`flex items-start space-x-2 w-full hover:bg-neutral-700 px-4 py-3 transition-colors ${i === 0 && "mt-4"}`}
                                                onClick={() => {
                                                    const htmlElem = document.querySelector(".overflow-y-auto");
                                                    htmlElem?.scrollTo({top: 0, behavior: "instant"});
                                                    setSelectedItemIndex(i)
                                                }}
                                            >
                                                <div
                                                    className={`font-bold ${epg.endTimestamp < DateHelper.GetDateTimeNowAsUnixTimespan() ? "text-[#008dd2]/60" : "text-[#008dd2]"}`}>
                                                    {DateHelper.GetHourAndMinuteFromUnixTimespanDate(epg.startTimestamp)}
                                                </div>
                                                <div
                                                    className={`flex flex-col text-left border-l border-neutral-600 pl-2 ${epg.endTimestamp < DateHelper.GetDateTimeNowAsUnixTimespan() ? "text-neutral-100/60" : "text-neutral-100"}`}>
                                                    <p>{epg.name}</p>
                                                    <p>{epg.category}</p>
                                                </div>
                                            </button>
                                        ) :
                                        <p className={"text-center pt-6 text-neutral-400"}>
                                            Brak danych na dzień {since}, <br/>zmień zakres czasu.</p>
                                    }
                                </div>
                                <div className={"w-2/3 px-4 overflow-y-scroll"}>
                                    {channel && selectedItemIndex !== -1 &&
                                        <div className={"animate-opacityEaseIn p-8 pt-14"} key={selectedItemIndex}>
                                            <p className={"text-[#008dd2] font-bold text-lg"}>{DateHelper.GetHourAndMinuteFromUnixTimespanDate(channel.epgList?.[selectedItemIndex].startTimestamp || 0)} - {DateHelper.GetHourAndMinuteFromUnixTimespanDate(channel.epgList?.[selectedItemIndex]?.endTimestamp || 0)}</p>
                                            <p className={"text-2xl md:text-3xl font-bold mt-1"}>{channel?.epgList?.[selectedItemIndex]?.name}</p>
                                            <span className={"flex space-x-2 items-center mt-1"}>
                                                <p className={"text-neutral-400"}>{channel?.epgList?.[selectedItemIndex]?.category}</p>
                                                {channel?.epgList?.[selectedItemIndex]?.rating !== 0 &&
                                                    <>
                                                        <span className={"h-1 w-1 rounded-full bg-neutral-100"}/>
                                                        <ClassificationIcon
                                                            age={channel?.epgList?.[selectedItemIndex]?.rating || 0}
                                                            className={"w-5"}
                                                        />
                                                    </>
                                                }
                                            </span>
                                            <div className={"w-20 h-1 bg-[#008dd2] rounded-full my-4"}/>
                                            <p>{channel?.epgList?.[selectedItemIndex]?.description || "Brak opisu"}</p>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </>
                }
            </div>
        </MainLayout>
    );
};

export default NewChannelDetailsPage;